import { InlineFeedback } from '../feedback/index.js';
import React from 'react';
import clsx from 'clsx';

const Select = React.forwardRef(({
  size = 'md',
  variant = 'default',
  error = false,
  disabled = false,
  className,
  label,
  helperText,
  errorMessage,
  children,
  ...props
}, ref) => {
  const baseClasses = 'block w-full border rounded-md shadow-sm transition-colors focus:outline-none focus:ring-1 bg-white';

  const variants = {
    default: 'border-gray-300 focus:border-blue-500 focus:ring-blue-500',
    filled: 'border-gray-300 bg-gray-50 focus:bg-white focus:border-blue-500 focus:ring-blue-500',
  };

  const sizes = {
    sm: 'px-3 py-2 text-sm',
    md: 'px-3 py-2 text-sm',
    lg: 'px-4 py-3 text-base',
  };

  const errorClasses = error ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : '';
  const disabledClasses = disabled ? 'bg-gray-50 text-gray-500 cursor-not-allowed' : '';

  const selectClasses = clsx(
    baseClasses,
    variants[variant],
    sizes[size],
    errorClasses,
    disabledClasses,
    className
  );
  const hasWrapper = Boolean(label || helperText || (error && errorMessage));

  const selectElement = (
    <select
      ref={ref}
      className={selectClasses}
      disabled={disabled}
      aria-invalid={error}
      aria-describedby={
        error && errorMessage ? 'select-error' : helperText ? 'select-helper' : undefined
      }
      {...props}
      data-otg-component={!hasWrapper ? (props['data-otg-component'] || 'Select') : undefined}
    >
      {children}
    </select>
  );

  if (hasWrapper) {
    return (
      <div data-otg-component={props['data-otg-component'] || 'Select'}>
        {label && (
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {label}
          </label>
        )}
        {selectElement}
        {error && errorMessage && (
          <InlineFeedback variant="error" id="select-error" className="mt-1 text-sm text-red-600">
            {errorMessage}
          </InlineFeedback>
        )}
        {!error && helperText && (
          <p id="select-helper" className="mt-1 text-sm text-gray-500">
            {helperText}
          </p>
        )}
      </div>
    );
  }

  return selectElement;
});

Select.displayName = 'Select';

export default Select;
